"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import {
    appGetItem,
    appSetItem,
    subscribeStorageAuthState,
} from "@/lib/auth-storage";

interface LayoutContextType {
    isSidebarCollapsed: boolean;
    toggleSidebar: () => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export function LayoutProvider({ children }: { children: React.ReactNode }) {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const readSavedState = () => {
        const savedState = appGetItem("sidebarCollapsed");
        if (savedState) {
            try {
                setIsSidebarCollapsed(JSON.parse(savedState));
            } catch {
                /* ignore malformed value */
            }
        }
    };

    useEffect(() => {
        readSavedState();
        // Re-read once sign-in state is known — see LanguageContext for why.
        return subscribeStorageAuthState(readSavedState);
    }, []);

    const toggleSidebar = () => {
        setIsSidebarCollapsed((prev) => {
            const newState = !prev;
            appSetItem("sidebarCollapsed", JSON.stringify(newState));
            return newState;
        });
    };

    return (
        <LayoutContext.Provider value={{ isSidebarCollapsed, toggleSidebar }}>
            {children}
        </LayoutContext.Provider>
    );
}

export function useLayout() {
    const context = useContext(LayoutContext);
    if (context === undefined) {
        throw new Error("useLayout must be used within a LayoutProvider");
    }
    return context;
}
